import type { mat4 } from 'gl-matrix';

import type { ReferencePlane } from '@/src/core/contours/referencePlane';

import * as d3 from 'd3';
import { computed, reactive, ref, toRefs, watch } from 'vue';

import { useVTKCallback } from '@/src/composables/useVTKCallback';
import { transform2dTo3d } from '@/src/core/contours/referencePlane';
import { useCurrentStudyStore } from '@/src/store/dicom-web/current-study-store';
import { useViewStore } from '@/src/store/views';
import { worldToSVG } from '@/src/utils/vtk-helpers';

export function image2DCordinateToWorld(
  x: number,
  y: number,
  indexToWorld: mat4,
): [number, number, number] {
  if (!indexToWorld) return [0, 0, 0];
  const referencePlane: ReferencePlane = { t: indexToWorld, _tInv: null };
  const point3D = transform2dTo3d([x - 0.5, y - 0.5], referencePlane);
  return point3D;
}

export function coordinateTransformation(props: {
  currentImageUid: string;
  id: string;
  canvasWidth: number;
  canvasHeight: number;
}) {
  const viewStore = useViewStore();
  const currentStudyStore = useCurrentStudyStore();

  const reactiveProps = reactive(props);
  const { currentImageUid, id, canvasWidth, canvasHeight }
    = toRefs(reactiveProps);

  const viewProxy = computed(() => viewStore.getViewProxy(id.value));
  const curImageMetadata = computed(() =>
    currentStudyStore.getVtkMetaData(currentImageUid.value),
  );

  const worldToIndex = computed(() => curImageMetadata.value.worldToIndex);
  const indexToWorld = computed(() => curImageMetadata.value.indexToWorld);

  // Kept for back-compat (axis-aligned bbox of the image in screen space).
  const imageCorners = ref({ left: [0, 0], right: [0, 0] });

  // Full 2D affine: image-pixel coords -> SVG/screen px (rotation + scale + translate).
  // Follows SVG matrix(a,b,c,d,e,f): sx = a*ix + c*iy + e ; sy = b*ix + d*iy + f
  const imageToScreenMatrix = ref({
    a: 1,
    b: 0,
    c: 0,
    d: 1,
    e: 0,
    f: 0,
    det: 1,
  });

  const isImageAvailable = computed(() =>
    currentStudyStore.isImageAvailable(currentImageUid.value),
  );

  const isMatrixInitialized = ref(false);

  const matrixReady = computed(() => {
    const m = imageToScreenMatrix.value;
    const isIdentity
      = m.a === 1 && m.b === 0 && m.c === 0 && m.d === 1 && m.e === 0 && m.f === 0;
    return isMatrixInitialized.value && !isIdentity && isImageAvailable.value;
  });

  function svgAt(ix: number, iy: number): number[] | null {
    const viewRenderer = viewProxy.value?.getRenderer();
    if (!viewRenderer) return null;
    const p3 = transform2dTo3d([ix, iy], {
      t: indexToWorld.value,
      _tInv: worldToIndex.value,
    });
    return worldToSVG(p3, viewRenderer);
  }

  const updateLeftCornerCoords = () => {
    if (!worldToIndex.value || !curImageMetadata.value.dimensions) return;
    if (!currentStudyStore.isImageAvailable(currentImageUid.value)) return;
    if (curImageMetadata.value.name === '(none)') return;

    // Sample the origin and one step along each image axis -> recovers the
    // basis vectors of the image in screen space (carries rotation + scale).
    const O = svgAt(-0.5, -0.5);
    const Xu = svgAt(0.5, -0.5);
    const Yu = svgAt(-0.5, 0.5);
    if (!O || !Xu || !Yu) return;
    if (Number.isNaN(O[0]) || Number.isNaN(O[1]) || Number.isNaN(Xu[0]) || Number.isNaN(Yu[1])) return;

    const a = Xu[0] - O[0];
    const b = Xu[1] - O[1]; // ex = screen delta per +1 image-x
    const c = Yu[0] - O[0];
    const d = Yu[1] - O[1]; // ey = screen delta per +1 image-y
    const det = a * d - c * b || 1;
    imageToScreenMatrix.value = { a, b, c, d, e: O[0], f: O[1], det };
    isMatrixInitialized.value = true;

    // back-compat bbox (still axis-aligned; only valid for un-rotated layout)
    const [dimX, dimY] = curImageMetadata.value.dimensions;
    const far = svgAt(dimX - 0.5, dimY - 0.5);
    imageCorners.value.left = [O[0], O[1]];
    if (far) imageCorners.value.right = far;
  };

  // forward: image-pixel coords -> screen px
  function imageToScreen(ix: number, iy: number): [number, number] {
    const { a, b, c, d, e, f } = imageToScreenMatrix.value;
    return [a * ix + c * iy + e, b * ix + d * iy + f];
  }

  // inverse: screen px -> image-pixel coords (rotation-safe hit-testing)
  function screenToImage(sx: number, sy: number): [number, number] {
    const { a, b, c, d, e, f, det } = imageToScreenMatrix.value;
    const dx = sx - e;
    const dy = sy - f;
    return [(dx * d - dy * c) / det, (-dx * b + dy * a) / det];
  }

  // Uniform px-per-image-pixel magnitude. Valid under rotation as long as the
  // on-screen scaling is isotropic (square pixels + uniform zoom).
  const scaleFactor = computed(() => {
    const { a, b } = imageToScreenMatrix.value;
    return Math.hypot(a, b) || 1;
  });

  // SVG/CSS transform for the overlay group (replaces `scale(scaleFactor)`).
  const overlayTransform = computed(() => {
    const { a, b, c, d, e, f } = imageToScreenMatrix.value;
    return `matrix(${a},${b},${c},${d},${e},${f})`;
  });

  interface ClientCoordinates {
    clientX: number;
    clientY: number;
  }

  function hasClientCoordinates(event: unknown): event is ClientCoordinates {
    return (
      typeof event === 'object'
      && event !== null
      && 'clientX' in event
      && typeof (event as Record<string, unknown>).clientX === 'number'
      && 'clientY' in event
      && typeof (event as Record<string, unknown>).clientY === 'number'
    );
  }

  // Absolute screen px (same space as worldToSVG / the matrix). Use as the
  // live-drawing buffer space, then convert at the boundaries with
  // screenToImage / imageToScreen.
  function mouseEventToScreen(payload: unknown): [number, number] {
    const container = viewProxy.value?.getContainer?.();
    if (container && hasClientCoordinates(payload)) {
      const rect = container.getBoundingClientRect();
      return [
        payload.clientX - rect.left - (container.clientLeft || 0),
        payload.clientY - rect.top - (container.clientTop || 0),
      ];
    }
    return d3.pointer(payload);
  }

  // Image coords (rotation-safe). Use for hit-testing against image geometry.
  function getCurrentCoordinates(payload: unknown) {
    const [px, py] = mouseEventToScreen(payload);
    return screenToImage(px, py);
  }

  // Image coords (kept for callers that want image space directly).
  function mouseEventToCoordinates(payload: unknown) {
    const [px, py] = mouseEventToScreen(payload);
    return screenToImage(px, py);
  }

  // Setup camera callback
  const cameraOnModified = useVTKCallback(
    computed(() => viewProxy.value?.getCamera().onModified.bind(viewProxy.value.getCamera())),
  );
  cameraOnModified(updateLeftCornerCoords);

  const viewOnResize = useVTKCallback(
    computed(() => viewProxy.value?.onResize?.bind(viewProxy.value)),
  );
  viewOnResize(updateLeftCornerCoords);

  // Setup watchers
  watch(
    [currentImageUid, canvasWidth, canvasHeight],
    () => {
      if (!currentStudyStore.isImageAvailable(currentImageUid.value)) {
        isMatrixInitialized.value = false;
      }
      updateLeftCornerCoords();
    },
    { immediate: true },
  );

  watch(
    isImageAvailable,
    (avail) => {
      if (avail) {
        updateLeftCornerCoords();
        requestAnimationFrame(updateLeftCornerCoords);
      }
      else {
        isMatrixInitialized.value = false;
      }
    },
  );

  watch(
    curImageMetadata,
    (meta) => {
      if (meta && meta.name !== '(none)') {
        updateLeftCornerCoords();
      }
    },
    { deep: true },
  );

  watch(
    viewProxy,
    (vp) => {
      if (vp) {
        updateLeftCornerCoords();
      }
    },
  );

  return {
    imageCorners,
    scaleFactor,
    imageToScreen,
    screenToImage,
    overlayTransform,
    imageToScreenMatrix,
    matrixReady,
    isImageAvailable,
    isMatrixInitialized,
    getCurrentCoordinates,
    mouseEventToCoordinates,
    mouseEventToScreen,
    updateLeftCornerCoords,
    curImageMetadata,
  };
}
